import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { T, TF } from "react-translator-component";
import { attachmentAcceptedExtension, validateAttachment } from "../../common/attachments";
import { colors } from "../../common/colors";
import { convertHeicToJpeg, getFileExtenction } from "../../common/files";
import appServices from "../../services/app-services/appServices";
import { setItem, setErrors } from "../../redux/Reducers/createPostReducer";
import { useAppDispatch } from "../../redux/store";
import { type Attachment } from "../../util/CreatePost/types";
import Section from "./Section";

const MAX_NUMBER_FILE_COUNT = 5;

export default function SelectAttachments() {
  const dispatch = useAppDispatch();

  const [files, setFiles] = useState<Attachment[]>([]);
  const [removedFiles, setRemovedFiles] = useState<Attachment[]>([]);
  const post = useSelector((state: any) => state.createPost.values);
  const error = useSelector((state: any) => state.createPost.errors.attachments);

  const handleFileUpload = (event: any) => {
    if (!event.target.files) {
      console.error("ファイルを取得できませんでした。", event)
      return
    }

    const selectedFiles: any[] = Array.from(event.target.files);
    if (selectedFiles.length === 0) {
      return
    }

    // 入力チェック
    let msg = ""
    const totalFiles = files.length + selectedFiles.length;
    if (totalFiles > MAX_NUMBER_FILE_COUNT) {
      msg = TF(T("error_file_count"), MAX_NUMBER_FILE_COUNT);
      selectedFiles.splice(MAX_NUMBER_FILE_COUNT - files.length);
    }

    let errMsg = ""
    for (let file of selectedFiles) {
      errMsg = validateAttachment(file)
      if (errMsg) {
        break
      }
    }

    if (errMsg) {
      dispatch(setErrors({ attachments: errMsg }));
    } else {
      dispatch(setErrors({ attachments: msg }));

      // 選択されたファイルをセット
      const newFiles = selectedFiles.map((file: any) => {
        if (getFileExtenction(file) === "heic") {
          return new Promise(async (resolve, reject) => {
            convertHeicToJpeg(file)
              .then((f: File) => resolve({
                name: f.name,
                file: f,
                _destroy: "0",
              }))
              .catch((e: any) => reject(e))
          })
        } else {
          let reader = new FileReader();
          return new Promise((resolve) => {
            reader.onload = (e: any) => {
              resolve({
                name: file.name,
                file: file,
                _destroy: "0",
              });
            };
            reader.readAsDataURL(file);
          });
        }
      });

      Promise.all(newFiles).then((loadedFiles) => {
        setFiles((prevFiles: any) => [...prevFiles, ...loadedFiles]);
      }).catch((e: any) => console.error(e));
    }
  };

  const removeFile = (index: number) => {
    dispatch(setErrors({ attachments: "" }));
    setFiles((prev: Attachment[]) =>
      prev.filter((_: Attachment, i: number) => i !== index)
    );
  };

  const saveRemoveFile = (file: Attachment) => {
    if (file.id !== undefined) {
      const item = file;
      const _removedFiles = [...removedFiles, { ...item, _destroy: "1" }];
      setRemovedFiles(_removedFiles);
    }
  };

  useEffect(() => {
    const _docsToUpload = files.filter((file: Attachment) => file.file !== undefined);
    dispatch(setItem({key: "attachments", value: [..._docsToUpload, ...removedFiles], error: error}));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, removedFiles]);

  useEffect(() => {
    if (!post?.id) return

    post.attachments.forEach((attachment: any) => {
      const filename = decodeURIComponent(attachment.name);
      appServices.getAttachment(post.id, attachment.id).then(blob => {
        const file = new File([blob], filename, { type: blob.type });
        setFiles((prevFiles: any) =>
          [
            ...prevFiles,
            { id: attachment.id, name: filename, file: file, _destroy: "0" }
          ].sort((a: any, b:any) => a.id - b.id));
      }).catch(() => {
        setFiles((prevFiles: any) =>
          [
            ...prevFiles,
            { id: attachment.id, name: filename, _destroy: "0" }
          ].sort((a: any, b:any) => a.id - b.id));
      });
    });
  }, []);

  return (
    <Section
      title={
        <>
          {T("upload_attachments")}
          <br />
          {`${T("upload_attachments_notice")} ${T("optional")}`}
        </>
      }
      body={
        <>
          <Typography style={{ marginTop: 20 }}>
            {T("upload_attachments_description")}
          </Typography>
          {error && <Typography style={{ color: colors.farm_red600, fontSize: "0.75rem" }}>{ error }</Typography>}
          <Box>
            {files?.map((item: Attachment, index: number) => (
              <div key={index} style={styles.postAttachmentList}>
                <Typography>
                  {item.name}
                </Typography>
                <DeleteIcon
                  style={{
                    color: "red",
                  }}
                  onClick={() => {
                    removeFile(index);
                    saveRemoveFile(item);
                  }}
                  color={"inherit"}
                />
              </div>
            ))}
          </Box>
          <Box textAlign="center">
            <Button
              disabled={files.length >= MAX_NUMBER_FILE_COUNT}
              color={"secondary"}
              sx={styles.uploadAttachmentButton}
              component="label"
              variant="outlined"
            >
              {T("select_attachments")}
              <input
                accept={attachmentAcceptedExtension}
                type="file"
                hidden
                multiple
                onChange={handleFileUpload}
              />
            </Button>
          </Box>
        </>
      }
    />
  )
}

const styles = {
  postAttachmentList: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "25px",
    marginTop: "10px",
    borderBottom: "1px solid #86BB57",
    padding: "5px 0",
  },
  uploadAttachmentButton: {
    width: 160,
    borderRadius: 20,
    borderWidth: 2,
    fontFamily: "mplus-1c-regular",
    marginTop: 5,
    color: colors.farm_black,
    backgroundColor: colors.farm_white,
  },
}
